import type CreativeSDK from '@cesdk/cesdk-js';
import { GQLProductSideAvailableDecorationType, GQLProductSideSideType } from '@theprintbar/graphql';
import {
	ArtworkAndMeta,
	CropSpec,
	Decoration,
	DecorationMeta,
	GraphicData,
	cropTransparentEdges,
	getClosestPantone,
	getPantoneColours,
	hexToPixel,
	isEMBDec,
	isSCRDec,
	isSingleRawImage,
	logError,
	mmToPixels,
	percentageOf,
	pixelsToMM,
} from '@theprintbar/shared';
//@ts-ignore
import { changeDpiBlob } from 'changedpi';
import { BoundingBox, getBlockMimeType, getOverlappingBoundingBox, getPage, getUserAddedBlocksOnPage } from './block';
import { getPageColours } from './colours';
import exclusionZones from './exclusionZones';
import { getGraphicData } from './graphic';
import recolourToors from './recolourTools';
import { MimeType } from './types';

const emptyArtworkAndMeta: ArtworkAndMeta = {
	artwork: new Blob([]),
	meta: {
		percentages: {
			width: 0,
			height: 0,
			centreOffset: 0,
			left: 0,
			top: 0,
			bottom: 0,
			right: 0,
		},
		width: 0,
		height: 0,
		left: 0,
		top: 0,
		stitches: 0,
		centreOffset: 0,
		colours: [],
	},
};

// For a single unmanipulated raster that overflows the page, crop the source
// image directly instead of asking CE.SDK to raster-export the whole page —
// which trips the device texture cap on large-format canvases (e.g. 1m×1.5m
// posters). Requires isSingleRawImage on the caller side so we can rely on
// a linear block-bbox → source-pixel ratio (no rotation/skew/flip/stroke).
//
// Two-phase design so we can fall back gracefully when the client can't
// decode a huge source at full DPI on-device: computeSourceCropRect always
// derives the pixel rectangle from block+overlap geometry, then
// renderSourceCropClientSide attempts the actual decode+crop. On failure
// (iOS OOM, silent subsample, decode throw) the caller forwards the rect as
// a CropSpec so the server can materialize it via sharp on demand.
function computeSourceCropRect(
	editor: CreativeSDK,
	blockId: number,
	page: number,
	overlap: BoundingBox,
): { uri: string; sx: number; sy: number; sw: number; sh: number; ow: number; oh: number } | null {
	const { block } = editor.engine;
	const fill = block.getFill(blockId);
	const sourceSet = block.getSourceSet(fill, 'fill/image/sourceSet');
	const originalSet = sourceSet?.find(s => !s.uri.includes('size=')) || sourceSet?.[0];
	if (!originalSet) return null;

	const blockPageLeft = block.getGlobalBoundingBoxX(blockId) - block.getGlobalBoundingBoxX(page);
	const blockPageTop = block.getGlobalBoundingBoxY(blockId) - block.getGlobalBoundingBoxY(page);
	const blockWidth = block.getGlobalBoundingBoxWidth(blockId);
	const blockHeight = block.getGlobalBoundingBoxHeight(blockId);
	if (!blockWidth || !blockHeight) return null;

	const scaleX = originalSet.width / blockWidth;
	const scaleY = originalSet.height / blockHeight;
	const sx = Math.max(0, Math.round((overlap.left - blockPageLeft) * scaleX));
	const sy = Math.max(0, Math.round((overlap.top - blockPageTop) * scaleY));
	const sw = Math.min(originalSet.width - sx, Math.round(overlap.width * scaleX));
	const sh = Math.min(originalSet.height - sy, Math.round(overlap.height * scaleY));
	if (sw <= 0 || sh <= 0) return null;

	return { uri: originalSet.uri, sx, sy, sw, sh, ow: originalSet.width, oh: originalSet.height };
}

// Uses createImageBitmap over `new Image()` so we (a) don't materialize the
// full RGBA buffer on the JS heap, and (b) get a clean decode failure instead
// of iOS Safari's silent subsample-then-succeed behavior on large sources.
// We verify decoded dimensions against the source's declared size as a belt-
// and-braces check for browsers that DO silently subsample; any mismatch or
// thrown decode returns null so the caller can fall back to a server crop.
async function renderSourceCropClientSide(rect: {
	uri: string;
	sx: number;
	sy: number;
	sw: number;
	sh: number;
	ow: number;
	oh: number;
}): Promise<{ blob: Blob; width: number; height: number } | null> {
	const res = await fetch(rect.uri);
	if (!res.ok) return null;
	const sourceBlob = await res.blob();

	let bitmap: ImageBitmap;
	try {
		bitmap = await createImageBitmap(sourceBlob);
	} catch (e) {
		console.warn('[renderSourceCropClientSide] createImageBitmap rejected — source too large for client decode:', e);
		return null;
	}

	try {
		if (bitmap.width !== rect.ow || bitmap.height !== rect.oh) {
			console.warn(
				`[renderSourceCropClientSide] browser subsampled source (${bitmap.width}×${bitmap.height} vs expected ${rect.ow}×${rect.oh}) — bailing to preserve DPI`,
			);
			return null;
		}

		const canvas = document.createElement('canvas');
		canvas.width = rect.sw;
		canvas.height = rect.sh;
		const ctx = canvas.getContext('2d');
		if (!ctx) return null;
		ctx.drawImage(bitmap, rect.sx, rect.sy, rect.sw, rect.sh, 0, 0, rect.sw, rect.sh);

		const outBlob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png'));
		if (!outBlob) return null;
		return { blob: outBlob, width: rect.sw, height: rect.sh };
	} finally {
		bitmap.close();
	}
}

interface GetArtworkProps {
	editor: CreativeSDK;
	page: number;
	decoration: Decoration;
	domain: string;
	dpi?: number;
	teamNames?: Array<{ id: string; name: string }>;
	colourHex?: string | null;
	isFinal?: boolean;
}

async function getArtwork({
	editor,
	page,
	decoration,
	dpi = 20,
	domain,
	colourHex,
	teamNames,
	isFinal,
}: GetArtworkProps): Promise<ArtworkAndMeta> {
	try {
		if (colourHex) {
			const pantones = await getPantoneColours();
			await recolourToors.recolourPage({
				editor,
				page,
				domain,
				colourList: [getClosestPantone(hexToPixel(colourHex), pantones)],
				replacements: {
					'*': getClosestPantone(hexToPixel(colourHex), pantones),
				},
			});
		}

		const { block } = editor.engine;
		const content = getUserAddedBlocksOnPage(editor, page, isFinal);
		const exclusions = block.getChildren(page).filter(child => exclusionZones.isExclusionBlock(child, editor));

		if (!content.length) {
			return emptyArtworkAndMeta;
		}

		if (teamNames) {
			teamNames.forEach(({ id, name }) => editor.engine.variable.setString(id, name));
			await new Promise(res => setTimeout(res, 10));
		}

		const { box, hasCutoff } = getOverlappingBoundingBox(editor, content, page) || {};
		//Nothing visible inside the pages
		if (!box) {
			return emptyArtworkAndMeta;
		}

		//box is page-local (0,0 = page top-left) — see getOverlappingBoundingBox
		const { top, left, width, height, bottom, right } = box;

		const scaleFactor = parseFloat(block.getMetadata(page, 'scaleFactor') || '1');
		const pageWidth = block.getWidth(page);
		const pageHeight = block.getHeight(page);

		let targetWidth = mmToPixels(pageWidth / scaleFactor, dpi);
		let targetHeight = mmToPixels(pageHeight / scaleFactor, dpi);
		let effectiveDpi = dpi;
		const maxDim = isFinal ? editor.engine.editor.getMaxExportSize() : 4095;

		if (targetWidth > maxDim || targetHeight > maxDim) {
			const scale = Math.min(maxDim / targetWidth, maxDim / targetHeight);
			targetWidth = Math.floor(targetWidth * scale);
			targetHeight = Math.floor(targetHeight * scale);
			//Recalculate effective dpi
			effectiveDpi = (targetWidth / (pageWidth / scaleFactor)) * 25.4;
		}

		let graphicData = content.map(b => getGraphicData(editor, page, b)).filter(Boolean) as GraphicData[];
		let soloImage =
			isFinal &&
			decoration.type !== GQLProductSideAvailableDecorationType.Dtf &&
			isSingleRawImage({
				graphicData,
				sceneItems: content.length,
			});
		let pageImage: Blob | null = null;
		let croppedFromSource = false;
		let cropSpec: CropSpec | undefined;

		if (soloImage) {
			const mimeType = await getBlockMimeType(editor, content[0]);
			if (mimeType?.includes('svg+xml')) {
				//Clear the solo image flag, and graphic data so we export the full page as a png
				soloImage = false;
				graphicData = [
					{
						...graphicData[0],
						designId: undefined,
					},
				];
			} else if (hasCutoff) {
				const rect = computeSourceCropRect(editor, content[0], page, box);
				if (rect) {
					try {
						const cropped = await renderSourceCropClientSide(rect);
						if (cropped) {
							pageImage = cropped.blob;
							effectiveDpi = (cropped.width / (width / scaleFactor)) * 25.4;
							croppedFromSource = true;
							console.log('Client-side source-crop succeeded', cropped.width, 'x', cropped.height, 'at', effectiveDpi, 'DPI');
						}
					} catch (e) {
						console.warn('[getArtwork] client-side source-crop threw:', e);
					}

					// Client couldn't render at full DPI (huge source on mobile, iOS
					// subsample, decode OOM). Forward the rect so the server can
					// sharp-extract it on demand — no downsampled PNG, no page export.
					if (!pageImage) {
						console.log('Using server-side crop spec for oversized source');
						cropSpec = { sx: rect.sx, sy: rect.sy, sw: rect.sw, sh: rect.sh };
						effectiveDpi = (rect.sw / (width / scaleFactor)) * 25.4;
						croppedFromSource = true;
					}
				}
			} else {
				// No cutoff: the source raster IS the artwork, unchanged. Skip
				// block.export entirely — it would try to rasterize the whole page
				// at full DPI (e.g. ~209 MP for a 1m×1.5m poster at 300 DPI) and
				// trip the WASM texture cap. Server's singleGraphic path already
				// references the source S3 asset directly. exportDPI comes from
				// the source-to-print ratio for correct metadata.
				const rect = computeSourceCropRect(editor, content[0], page, box);
				if (rect) {
					effectiveDpi = (rect.sw / (width / scaleFactor)) * 25.4;
					croppedFromSource = true;
				}
			}
		}

		if (!pageImage && !cropSpec && !soloImage) {
			//Remove the page outline
			const outlineColour = block.getStrokeColor(page);
			block.setStrokeColor(page, {
				a: 0,
				b: 0,
				g: 0,
				r: 0,
			});
			exclusions.forEach(exclusion => block.setOpacity(exclusion, 0));
			while (!pageImage) {
				try {
					//Make the exclusions invisible for the artwork export
					pageImage = await block.export(page, {
						mimeType: MimeType.Png,
						targetWidth,
						targetHeight,
					});
				} catch (e) {
					const message = await logError(e);
					if (!message.includes('resources')) {
						throw e;
					} else {
						//Reduce the target width and height by 10% and try again
						targetWidth = Math.floor(targetWidth * 0.9);
						targetHeight = Math.floor(targetHeight * 0.9);
						effectiveDpi = (targetWidth / (pageWidth / scaleFactor)) * 25.4;
					}
					await new Promise(res => setTimeout(res, 100));
				}
			}
			//Restore the outline colour
			block.setStrokeColor(page, outlineColour);
			//Restore the exclusions opacity
			exclusions.forEach(exclusion => block.setOpacity(exclusion, 1));
		}

		//CentreOffset is the distance from the centre of the design to the centre of the print area
		//We know where the left of the design is, so we can calculate the centre of the design
		const centreOffset = left + width / 2 - pageWidth / 2;

		const meta: DecorationMeta = {
			graphicData,
			sceneItems: content.length,
			percentages: {
				width: percentageOf(width, pageWidth),
				height: percentageOf(height, pageHeight),
				left: percentageOf(left, pageWidth),
				centreOffset: percentageOf(centreOffset, pageWidth),
				top: percentageOf(top, pageHeight),
			},
			width: Math.round(width / scaleFactor),
			height: Math.round(height / scaleFactor),
			left: Math.round(left / scaleFactor),
			top: Math.round(top / scaleFactor),
			centreOffset: Math.round(centreOffset / scaleFactor),
			bottom: Math.round(bottom / scaleFactor),
			right: Math.round(right / scaleFactor),
			colours: await getPageColours(editor, decoration.side),
			stitches: 0,
		};

		if (pageImage && !croppedFromSource) {
			const cImage = await cropTransparentEdges(pageImage);
			pageImage = cImage.blob;
			const newCentreOffset = cImage.left + cImage.width / 2 - targetWidth / 2;

			meta.width = pixelsToMM(Math.round(cImage.width), effectiveDpi);
			meta.height = pixelsToMM(Math.round(cImage.height), effectiveDpi);
			meta.left = pixelsToMM(Math.round(cImage.left), effectiveDpi);
			meta.top = pixelsToMM(Math.round(cImage.top), effectiveDpi);
			meta.bottom = pixelsToMM(Math.round(cImage.bottom), effectiveDpi);
			meta.right = pixelsToMM(Math.round(cImage.right), effectiveDpi);
			meta.centreOffset = pixelsToMM(Math.round(newCentreOffset), effectiveDpi);
			meta.percentages = {
				width: percentageOf(cImage.width, pageWidth),
				height: percentageOf(cImage.height, pageHeight),
				left: percentageOf(meta.left, pageWidth),
				centreOffset: percentageOf(newCentreOffset, pageWidth),
				top: percentageOf(meta.top, pageHeight),
			};
			meta.exportDPI = Math.floor(effectiveDpi);
		} else if (croppedFromSource) {
			// Any source-driven path (client-side crop success, server cropSpec, or
			// solo-image-no-cutoff). meta.width/height/etc. above are already
			// correct in mm (derived from the overlap box); we just need exportDPI.
			meta.exportDPI = Math.floor(effectiveDpi);
		}

		if (!pageImage?.size && !soloImage && !cropSpec) {
			if (isFinal) {
				throw 'Failed to export artwork image';
			}
			return emptyArtworkAndMeta;
		}

		if (teamNames) {
			teamNames.forEach(({ id }) => editor.engine.variable.setString(id, teamNames.find(t => t.id === id)?.name || ''));
		}

		//Download the file locally
		if (isFinal && pageImage) {
			pageImage = (await changeDpiBlob(pageImage, effectiveDpi)) as Blob;
		}

		// On final export, also emit a PDF/X-4 print-ready file cropped to the same
		// content region as the PNG. Gated to decoration types where vector output
		// matters (EMB, SCR) or where the PNG had to be downsampled below 200 DPI
		// (large canvases hitting the raster export size cap). DTG/DTF at full DPI
		// print from the PNG directly and don't need the PDF pass.
		//
		// CE.SDK PDF export renders the whole page block, so we temporarily shrink
		// the page to the content bbox and shift the user content so its top-left
		// lands on the page origin. The @imgly/plugin-print-ready-pdfs-web plugin
		// then converts the RGB PDF to CMYK with ICC embedding and PDF/X-4 markers.
		// Non-fatal — the PNG remains the primary artwork.
		const needsVectorPdf = isEMBDec(decoration) || isSCRDec(decoration);
		let printReady: Blob | undefined;
		if (isFinal && !soloImage && needsVectorPdf) {
			console.log('Generating PDF');
			const savedStroke = block.getStrokeColor(page);
			const savedPageWidth = block.getWidth(page);
			const savedPageHeight = block.getHeight(page);
			const pageGlobalX = block.getGlobalBoundingBoxX(page);
			const pageGlobalY = block.getGlobalBoundingBoxY(page);
			// Positions are local to parent; page hasn't moved, so global delta == local delta.
			const shiftX = left - pageGlobalX;
			const shiftY = top - pageGlobalY;
			const savedPositions = content.map(id => ({
				id,
				x: block.getPositionX(id),
				y: block.getPositionY(id),
			}));

			try {
				block.setStrokeColor(page, { a: 0, b: 0, g: 0, r: 0 });
				exclusions.forEach(e => block.setOpacity(e, 0));
				if (shiftX !== 0 || shiftY !== 0) {
					savedPositions.forEach(({ id, x, y }) => {
						block.setPositionX(id, x - shiftX);
						block.setPositionY(id, y - shiftY);
					});
				}
				block.setWidth(page, width);
				block.setHeight(page, height);

				// Match the PNG's effective DPI so the rasterized parts of the PDF
				// (high-compatibility mode) render at the same resolution.
				const pdfTargetWidth = Math.max(1, Math.round(mmToPixels(width / scaleFactor, effectiveDpi)));
				const pdfTargetHeight = Math.max(1, Math.round(mmToPixels(height / scaleFactor, effectiveDpi)));

				const rgbPdf = await block.export(page, {
					mimeType: MimeType.Pdf,
					targetWidth: pdfTargetWidth,
					targetHeight: pdfTargetHeight,
				});
				const { convertToPDFX } = await import('@imgly/plugin-print-ready-pdfs-web');
				printReady = await convertToPDFX(rgbPdf, {
					outputProfile: 'fogra39',
					title: `${decoration.side} artwork`,
				});
			} catch (e) {
				console.warn('[getArtwork] PDF/X-4 print-ready export failed; keeping PNG only:', e);
			} finally {
				savedPositions.forEach(({ id, x, y }) => {
					block.setPositionX(id, x);
					block.setPositionY(id, y);
				});
				block.setWidth(page, savedPageWidth);
				block.setHeight(page, savedPageHeight);
				block.setStrokeColor(page, savedStroke);
				exclusions.forEach(e => block.setOpacity(e, 1));
			}
		}

		return {
			artwork: pageImage ? new File([pageImage], 'artwork.png', { type: MimeType.Png }) : undefined,
			printReady: printReady ? new File([printReady], 'artwork.pdf', { type: MimeType.Pdf }) : undefined,
			cropSpec,
			meta,
		};
	} catch (e) {
		if (isFinal) {
			throw e;
		}
		return emptyArtworkAndMeta;
	}
}

export function pageHasArtwork(editor: CreativeSDK, side: GQLProductSideSideType) {
	const page = getPage(editor, side);
	return !!editor.engine.block.getChildren(page).length;
}

export async function getArtworkFromPage(props: Omit<GetArtworkProps, 'page'>) {
	const page = getPage(props.editor, props.decoration.side);
	const design = await getArtwork({ ...props, page });
	return design;
}

export default {
	getArtwork,
	getArtworkFromPage,
};
